import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { useShallow } from 'zustand/react/shallow'
import type { AutomationPlatform, DayOfWeek } from '@backend/lib/db/schema/automation'

export type DraftSchedule = Partial<Record<DayOfWeek, AutomationPlatform[]>>

export interface LogoCandidate {
  url?: string
  svg?: string
  dataUrl?: string
  type: 'img' | 'svg' | 'favicon' | 'og-image'
  score: number
  source: string
  width?: number
  height?: number
}

export interface ColorCandidate {
  hex: string
  rgb: { r: number; g: number; b: number }
  frequency: number
  score: number
  sources: string[]
}

export interface CompanyAnalysis {
  name: string
  description: string
  website: string
  industry: string
  products: string[]
  services: string[]
  valueProps: string[]
  targetAudience: string[]
  brandVoice: string
  keyThemes: string[]
  socialLinks?: {
    linkedin?: string
    twitter?: string
    instagram?: string
    facebook?: string
  }
}

interface CompanySetupState {
  // Data
  url: string
  companyAnalysis: CompanyAnalysis | null
  logoCandidates: LogoCandidate[]
  selectedLogos: LogoCandidate[]
  colorCandidates: ColorCandidate[]
  selectedColors: ColorCandidate[]

  // Posting cadence (collected during onboarding, fanned out into automations
  // after the persona is created)
  draftSchedule: DraftSchedule
  sendReminders: boolean

  // Voice & Audience
  voice: string
  audience: string

  // Post defaults
  cta: string
  hashtags: string
  links: string
  useEmojis: boolean

  // Final
  referralSource: string
  receiveMarketing: boolean

  // Loading states (for parallel API calls)
  isLoadingCompany: boolean
  isLoadingLogos: boolean
  isLoadingColors: boolean

  error: string | null

  // Actions
  setUrl: (url: string) => void
  setCompanyAnalysis: (data: CompanyAnalysis | null) => void
  patchCompanyAnalysis: (partial: Partial<CompanyAnalysis>) => void
  setLogoCandidates: (logos: LogoCandidate[]) => void
  toggleLogoSelection: (logo: LogoCandidate) => void
  setColorCandidates: (colors: ColorCandidate[]) => void
  toggleColorSelection: (color: ColorCandidate) => void
  updateSelectedColor: (index: number, hex: string) => void
  addCustomColor: (hex: string) => void
  removeSelectedColor: (index: number) => void
  addCustomLogo: (url: string) => void
  setDraftSchedule: (schedule: DraftSchedule) => void
  setSendReminders: (send: boolean) => void
  setVoice: (voice: string) => void
  setAudience: (audience: string) => void
  setCta: (cta: string) => void
  setHashtags: (hashtags: string) => void
  setLinks: (links: string) => void
  setUseEmojis: (use: boolean) => void
  setReferralSource: (source: string) => void
  setReceiveMarketing: (receive: boolean) => void
  setLoadingState: (key: 'company' | 'logos' | 'colors', loading: boolean) => void
  setError: (error: string | null) => void
  reset: () => void
  clearStorage: () => void
}

const initialState = {
  url: '',
  companyAnalysis: null,
  logoCandidates: [],
  selectedLogos: [],
  colorCandidates: [],
  selectedColors: [],
  draftSchedule: {} as DraftSchedule,
  sendReminders: false,
  voice: '',
  audience: '',
  cta: '',
  hashtags: '',
  links: '',
  useEmojis: true,
  referralSource: '',
  receiveMarketing: false,
  isLoadingCompany: false,
  isLoadingLogos: false,
  isLoadingColors: false,
  error: null,
}

const companySetupStore = create<CompanySetupState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUrl: (url) => set({ url }),

      setCompanyAnalysis: (data) => {
        // Prefill voice and audience from analysis
        const voice = data?.brandVoice || ''
        const audience = data?.targetAudience?.length
          ? data.targetAudience.map((item) => `• ${item}`).join('\n')
          : ''
        set({ companyAnalysis: data, voice, audience })
      },

      // User-driven edits: only patch the named fields. Creates a blank
      // analysis if one doesn't exist yet (e.g. the AI extraction failed),
      // so the summary step remains fully editable without a re-run.
      patchCompanyAnalysis: (partial) => {
        const { companyAnalysis } = get()
        const base: CompanyAnalysis = companyAnalysis ?? {
          name: '',
          description: '',
          website: '',
          industry: '',
          products: [],
          services: [],
          valueProps: [],
          targetAudience: [],
          brandVoice: '',
          keyThemes: [],
        }
        set({ companyAnalysis: { ...base, ...partial } })
      },

      setLogoCandidates: (logos) => {
        // Auto-select the first logo
        const firstLogo = logos[0]
        const autoSelected = firstLogo ? [firstLogo] : []
        set({ logoCandidates: logos, selectedLogos: autoSelected })
      },

      toggleLogoSelection: (logo) => {
        const { selectedLogos } = get()
        const logoKey = logo.url || logo.svg || ''
        const isSelected = selectedLogos.some((l) => (l.url || l.svg || '') === logoKey)

        if (isSelected) {
          set({
            selectedLogos: selectedLogos.filter((l) => (l.url || l.svg || '') !== logoKey),
          })
        } else {
          set({ selectedLogos: [...selectedLogos, logo] })
        }
      },

      setColorCandidates: (colors) => {
        // Auto-select the first 5 colors
        const autoSelected = colors.slice(0, 5)
        set({ colorCandidates: colors, selectedColors: autoSelected })
      },

      toggleColorSelection: (color) => {
        const { selectedColors } = get()
        const isSelected = selectedColors.some((c) => c.hex === color.hex)

        if (isSelected) {
          set({
            selectedColors: selectedColors.filter((c) => c.hex !== color.hex),
          })
        } else {
          set({ selectedColors: [...selectedColors, color] })
        }
      },

      updateSelectedColor: (index, hex) => {
        const { selectedColors } = get()
        const existing = selectedColors[index]
        if (!existing) return

        // Convert hex to RGB
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        const updated = [...selectedColors]
        updated[index] = {
          hex,
          rgb: { r, g, b },
          frequency: existing.frequency,
          score: existing.score,
          sources: existing.sources,
        }
        set({ selectedColors: updated })
      },

      addCustomColor: (hex) => {
        const { selectedColors } = get()
        // Convert hex to RGB
        const r = parseInt(hex.slice(1, 3), 16)
        const g = parseInt(hex.slice(3, 5), 16)
        const b = parseInt(hex.slice(5, 7), 16)
        const newColor: ColorCandidate = {
          hex,
          rgb: { r, g, b },
          frequency: 0,
          score: 0,
          sources: ['custom'],
        }
        set({ selectedColors: [...selectedColors, newColor] })
      },

      removeSelectedColor: (index) => {
        const { selectedColors } = get()
        set({ selectedColors: selectedColors.filter((_, i) => i !== index) })
      },

      addCustomLogo: (url) => {
        const { selectedLogos } = get()
        const newLogo: LogoCandidate = {
          url,
          type: 'img',
          score: 1,
          source: 'custom',
        }
        set({ selectedLogos: [...selectedLogos, newLogo] })
      },

      setDraftSchedule: (schedule) => set({ draftSchedule: schedule }),

      setSendReminders: (send) => set({ sendReminders: send }),

      setVoice: (voice) => set({ voice }),

      setAudience: (audience) => set({ audience }),

      setCta: (cta) => set({ cta }),

      setHashtags: (hashtags) => set({ hashtags }),

      setLinks: (links) => set({ links }),

      setUseEmojis: (use) => set({ useEmojis: use }),

      setReferralSource: (source) => set({ referralSource: source }),

      setReceiveMarketing: (receive) => set({ receiveMarketing: receive }),

      setLoadingState: (key, loading) => {
        switch (key) {
          case 'company':
            set({ isLoadingCompany: loading })
            break
          case 'logos':
            set({ isLoadingLogos: loading })
            break
          case 'colors':
            set({ isLoadingColors: loading })
            break
        }
      },

      setError: (error) => set({ error }),

      reset: () => set(initialState),

      clearStorage: () => {
        localStorage.removeItem('company-setup-storage')
        set(initialState)
      },
    }),
    {
      name: 'company-setup-storage',
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

export const useCompanySetupStore = () => companySetupStore(useShallow((s) => s))
